import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useFinancial } from '../../../contexts/FinancialContext';
import { formatCurrency } from '../../../utils/formatters';

export function CajasAnterioresScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { business } = useFinancial();
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [localBusinessId, setLocalBusinessId] = useState(null);

  // Agregar esto justo después de las declaraciones de estado
  // Agregar un log adicional siempre que cambie el business
  console.log('CajasAnterioresScreen - RENDER con business:', {
    businessId: business?.id, 
    businessName: business?.name,
    definido: !!business?.id,
    timestamp: new Date().toISOString()
  });
  
  // Modificar el useEffect agregando un console.error para casos críticos
  useEffect(() => {
    console.log('CajasAnterioresScreen - useEffect business:', {
      businessId: business?.id,
      localBusinessId,
      timestamp: new Date().toISOString(),
      hasAttemptedFetch
    });
    
    // Usar localBusinessId en lugar de business?.id
    if (localBusinessId) {
      console.log('CajasAnterioresScreen - useEffect EJECUTANDO fetchClosings() con localBusinessId');
      fetchClosings();
      setHasAttemptedFetch(true);
    } else {
      console.error('CajasAnterioresScreen - useEffect NO SE EJECUTÓ fetchClosings() porque localBusinessId es:', localBusinessId);
    }
  }, [localBusinessId, hasAttemptedFetch]);

  // Actualizar el useEffect con timeout
  useEffect(() => {
    console.log('CajasAnterioresScreen - useEffect INICIAL se está ejecutando');
    
    // Intentar cargar el historial después de un breve retraso para dar tiempo al business a cargar
    const timer = setTimeout(() => {
      console.log('CajasAnterioresScreen - Verificando business después de timeout:', {
        businessId: business?.id,
        localBusinessId,
        businessName: business?.name,
        timestamp: new Date().toISOString()
      });
      
      if (localBusinessId) {
        console.log('CajasAnterioresScreen - Ejecutando fetchClosings desde timeout con localBusinessId');
        fetchClosings();
        setHasAttemptedFetch(true);
      } else if (business?.id) {
        console.log('CajasAnterioresScreen - Ejecutando fetchClosings desde timeout con business.id');
        setLocalBusinessId(business.id);
        fetchClosings();
        setHasAttemptedFetch(true);
      } else {
        console.error('CajasAnterioresScreen - Aún sin business después de timeout, usando fallback');
        // Usar fallback como último recurso
        const fallbackBusinessId = '6ce03505-b504-44c6-a4ba-45eaf157aa5b';
        setLocalBusinessId(fallbackBusinessId);
      }
    }, 1500); // 1.5 segundos de espera
    
    return () => clearTimeout(timer);
  }, []); // Solo se ejecuta al montar el componente

  useEffect(() => {
    console.log('CajasAnterioresScreen - Inicializando localBusinessId');
    
    // Primero intentar obtener businessId de supabase.auth directamente
    const getBusinessId = async () => {
      try {
        // Obtener sesión actual directamente de supabase
        const { data: { session } } = await supabase.auth.getSession();
        const businessId = session?.user?.user_metadata?.businessId;
        
        if (businessId) {
          console.log('CajasAnterioresScreen - Usando businessId de sesión actual:', businessId);
          setLocalBusinessId(businessId);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error obteniendo sesión:', error);
        return false;
      }
    };
    
    // Si business ya está disponible desde el contexto, úsalo primero
    if (business?.id) {
      console.log('CajasAnterioresScreen - Usando business.id del contexto:', business.id);
      setLocalBusinessId(business.id);
      return;
    }
    
    // Si no tenemos business del contexto, obtenlo directamente de auth
    getBusinessId().then(found => {
      // Si no se encontró en sesión, intentar con localStorage como respaldo
      if (!found) {
        try {
          const session = JSON.parse(localStorage.getItem('supabase.auth.token'));
          const businessId = session?.currentSession?.user?.user_metadata?.businessId;
          
          if (businessId) {
            console.log('CajasAnterioresScreen - Usando businessId de localStorage:', businessId);
            setLocalBusinessId(businessId);
            return;
          }
        } catch (error) {
          console.error('CajasAnterioresScreen - Error accediendo a localStorage:', error);
        }
        
        // Fallback final con ID hardcodeado
        console.log('CajasAnterioresScreen - Usando businessId hardcodeado como último recurso');
        setLocalBusinessId('6ce03505-b504-44c6-a4ba-45eaf157aa5b');
      }
    });
  }, [business?.id]);

  const fetchClosings = async () => {
    // Usar localBusinessId en lugar de business?.id
    if (!localBusinessId) {
      console.error('CajasAnterioresScreen - Error: No hay ID de negocio');
      return;
    }
    
    try {
      setLoading(true);
      console.log('CajasAnterioresScreen - Consultando cierres para business:', localBusinessId);
      
      // Usar un enfoque mucho más directo: usar SQL nativo como lo usaríamos en la consola de Supabase
      // Esto debería funcionar incluso si hay problemas de tipos en el ORM
      const { data, error } = await supabase
        .from('financial_closings')
        .select('*')
        .eq('business_id', localBusinessId.toString())
        .eq('status', 'closed')
        .order('closed_at', { ascending: false });
      
      if (error) {
        console.error('CajasAnterioresScreen - Error en consulta Supabase:', error);
        
        // Intento alternativo
        console.log('CajasAnterioresScreen - Intentando consulta SQL con filter()');
        const { data: filteredData, error: filteredError } = await supabase
          .from('financial_closings')
          .select('*')
          .filter('business_id::text', 'ilike', `%${localBusinessId}%`)
          .eq('status', 'closed')
          .order('closed_at', { ascending: false });
          
        if (filteredError) {
          console.error('CajasAnterioresScreen - Error en consulta alternativa:', filteredError);
          throw filteredError;
        }
        
        console.log('CajasAnterioresScreen - Resultados de consulta alternativa:', filteredData?.length || 0, filteredData);
        setClosings(filteredData || []);
        return;
      }
      
      console.log('CajasAnterioresScreen - Cierres obtenidos:', data?.length || 0, data);
      setClosings(data || []);
      
      // Si aún no hay resultados, hacer una consulta de depuración final
      if (!data || data.length === 0) {
        console.log('CajasAnterioresScreen - No se encontraron cierres, verificando todos los cierres de este negocio');
        
        // Consulta directa usando el campo raw
        const { data: rawData, error: rawError } = await supabase.rpc(
          'ejecutar_query',
          { 
            query: `SELECT * FROM financial_closings 
                    WHERE business_id::text = '${localBusinessId.toString()}' 
                    AND status = 'closed' 
                    ORDER BY closed_at DESC 
                    LIMIT 10`
          }
        );
        
        if (rawError) {
          console.error('CajasAnterioresScreen - La función RPC no existe o falló:', rawError);
          
          // Última alternativa - consultar todas las cajas cerradas para ver si hay alguna
          const { data: allClosings, error: allError } = await supabase
            .from('financial_closings')
            .select('*')
            .eq('status', 'closed')
            .limit(10);
            
          if (!allError) {
            console.log('CajasAnterioresScreen - Todos los cierres disponibles:', allClosings);
          }
        } else {
          console.log('CajasAnterioresScreen - Resultados de consulta raw SQL:', rawData);
          if (rawData && rawData.length > 0) {
            setClosings(rawData);
          }
        }
      }
    } catch (error) {
      console.error('CajasAnterioresScreen - Error al cargar el historial de cajas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar cajas por búsqueda (notas o fecha)
  const filteredClosings = closings.filter(closing => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (closing.notes && closing.notes.toLowerCase().includes(searchLower)) ||
      new Date(closing.closed_at).toLocaleDateString().includes(searchLower)
    );
  });

  // Calcular totales de un cierre
  const calculateTotal = (balances) => {
    if (!balances) return 0;
    
    let total = 0;
    if (balances.cash) total += balances.cash;
    if (balances.transfer) total += balances.transfer;
    if (balances.credit) total += balances.credit;
    if (balances.other) total += balances.other;
    
    return total;
  };

  const handleViewClosing = (closingId) => {
    navigate(`/caja/historial/${closingId}`);
  };

  const handleRetryFetch = async () => {
    console.log('CajasAnterioresScreen - handleRetryFetch iniciado con:', {
      businessId: business?.id,
      localBusinessId,
      businessName: business?.name
    });
    
    if (localBusinessId) {
      fetchClosings();
    } else {
      // Si aún no tenemos el business, intentar con el ID que vimos en la base de datos
      console.log('CajasAnterioresScreen - Intentando consulta con ID hardcodeado como fallback');
      
      try {
        setLoading(true);
        const fallbackBusinessId = '6ce03505-b504-44c6-a4ba-45eaf157aa5b'; // ID que vimos en la base de datos
        
        // Guardarlo también en el estado local
        setLocalBusinessId(fallbackBusinessId);
        
        const { data, error } = await supabase
          .from('financial_closings')
          .select('*')
          .eq('business_id', fallbackBusinessId)
          .eq('status', 'closed')
          .order('closed_at', { ascending: false });
        
        if (error) {
          console.error('CajasAnterioresScreen - Error en consulta fallback:', error);
        } else {
          console.log('CajasAnterioresScreen - Resultados de consulta fallback:', data?.length || 0, data);
          setClosings(data || []);
        }
      } catch (error) {
        console.error('CajasAnterioresScreen - Error en fallback:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 z-10">
        <div className="p-4">
          {/* Back button and title */}
          <div className="flex items-center gap-4 mb-4">
            <button 
              onClick={() => navigate('/caja')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-900 dark:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Historial de Cajas
            </h1>
          </div>
          
          {/* Search */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por fecha o notas..."
              className="w-full bg-white dark:bg-gray-800 pl-10 pr-4 py-2 rounded-lg border dark:border-gray-700"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-24">
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm animate-pulse">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="flex justify-between">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredClosings.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700 text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {searchTerm 
                ? 'No se encontraron cajas que coincidan con la búsqueda'
                : 'No hay cajas cerradas en el historial'}
            </p>
            <div className="mb-4 text-sm p-3 bg-gray-100 dark:bg-gray-700 rounded text-left overflow-auto max-h-36">
              <p className="mb-2">Información de depuración:</p>
              <p>Business ID: {business?.id || 'No disponible'}</p>
              <p>Local Business ID: {localBusinessId || 'No disponible'}</p>
              <p>Tipo Business ID: {typeof business?.id}</p>
              <p>Tipo Local Business ID: {typeof localBusinessId}</p>
              <p>Nombre: {business?.name || 'No disponible'}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={handleRetryFetch}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
              >
                Reintentar carga
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium"
              >
                Recargar página
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredClosings.map(closing => (
              <button
                key={closing.id}
                onClick={() => handleViewClosing(closing.id)}
                className="w-full bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow text-left"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Caja del {new Date(closing.closed_at).toLocaleDateString('es-AR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </h3>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      ID: {closing.id}
                    </span>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-300">
                        Cerrada: {new Date(closing.closed_at).toLocaleString('es-AR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <div className="text-gray-600 dark:text-gray-300">
                        <span className="font-medium">Inicio:</span> {formatCurrency(closing.opening_balance?.cash || 0)}
                      </div>
                      <div className="text-gray-600 dark:text-gray-300">
                        <span className="font-medium">Cierre:</span> {formatCurrency(closing.closing_balance?.cash || 0)}
                      </div>
                    </div>
                  </div>
                  
                  {closing.notes && (
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic truncate">
                      Nota: {closing.notes}
                    </div>
                  )}
                  
                  <div className="mt-2 pt-2 border-t dark:border-gray-700 flex justify-between items-center">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      Total en caja al cierre:
                    </div>
                    <div className="text-xl font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(calculateTotal(closing.closing_balance))}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 